import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData } from "services/apiTypes";

export type ProgressStatus = "not_started" | "in_progress" | "completed" | "inactive";
export type AccessStatus = "active" | "not_started" | "expired";

export type CourseAnalytics = {
  course_id: number;
  course_title: string;
  total_students: number;
  active_students: number;
  completed_students: number;
  average_progress_percentage: number;
  average_quiz_score: number;
  total_items: number;
  completed_items_count: number;
  not_started_students_count: number;
  in_progress_students_count: number;
  inactive_students_count: number;
  last_activity_at: string | null;
  has_content: boolean;
};

export type StudentProgressRow = {
  student_id: number;
  student_name: string;
  phone?: string | null;
  enrolled_at: string | null;
  access_status: AccessStatus;
  progress_percentage: number;
  completed_items_count: number;
  total_items_count: number;
  last_accessed_at: string | null;
  completed_at: string | null;
  quizzes_attempted_count: number;
  average_quiz_score: number;
  last_quiz_score: number | null;
  videos_watched_count: number;
  files_completed_count: number;
  images_completed_count: number;
  status: ProgressStatus;
};

export type ItemProgressDetail = {
  item_id: number;
  item_title: string;
  item_type: string;
  progress: {
    status: string;
    progress_percentage: number;
    progress_seconds: number | null;
    duration_seconds: number | null;
    first_opened_at: string | null;
    last_accessed_at: string | null;
    completed_at: string | null;
  };
};

export type QuizAttemptRow = {
  id: number;
  quiz_id: number;
  quiz_title: string | null;
  status: string;
  score: number;
  total_questions: number;
  correct_answers_count: number;
  wrong_answers_count: number;
  started_at: string | null;
  submitted_at: string | null;
};

export type StudentCourseProgressDetail = {
  course: { id: number; title: string };
  student: { id: number; name: string; phone?: string | null };
  summary: StudentProgressRow;
  course_progress: {
    completed_items_count: number;
    total_items_count: number;
    progress_percentage: number;
    last_accessed_at: string | null;
    completed_at: string | null;
  };
  items: ItemProgressDetail[];
  quiz_attempts: QuizAttemptRow[];
};

export type StudentLearningSummary = {
  student: { id: number; name: string; phone?: string | null };
  enrolled_courses_count: number;
  completed_courses_count: number;
  average_progress_percentage: number;
  last_activity_at: string | null;
  courses: Array<
    StudentProgressRow & {
      course_id: number;
      course_title: string;
      instructor_name?: string | null;
    }
  >;
};

export type AdminCourseAnalyticsRow = {
  course_id: number;
  course_title: string;
  instructor_id: number | null;
  instructor_name: string | null;
  students_count: number;
  total_items: number;
  average_progress_percentage: number;
  average_quiz_score: number;
  completed_students_count: number;
  last_activity_at: string | null;
};

export type AdminLearningOverview = {
  total_active_learners: number;
  total_enrollments: number;
  average_progress_percentage: number;
  completed_course_enrollments_count: number;
  average_quiz_score: number;
  recently_active_students_count: number;
  courses: AdminCourseAnalyticsRow[];
  courses_pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export type StudentsProgressQuery = {
  page?: number;
  per_page?: number;
  search?: string;
  progress_status?: ProgressStatus | "";
  access_status?: AccessStatus | "";
  date_from?: string;
  date_to?: string;
};

function unwrapPaginatedStudents(payload: ApiEnvelope<PaginatedResult<StudentProgressRow>> | undefined) {
  const result = payload?.result as unknown as Record<string, unknown> | undefined;
  if (!result) {
    return {
      data: [],
      meta: { current_page: 1, last_page: 1, per_page: 20, total: 0, from: null, to: null },
    } as PaginatedResult<StudentProgressRow>;
  }

  if (Array.isArray(result.data) && typeof result.current_page === "number") {
    return {
      data: result.data as StudentProgressRow[],
      meta: {
        current_page: result.current_page as number,
        last_page: result.last_page as number,
        per_page: result.per_page as number,
        total: result.total as number,
        from: (result.from as number | null) ?? null,
        to: (result.to as number | null) ?? null,
      },
    };
  }

  const nested = result as unknown as PaginatedResult<StudentProgressRow>;
  if (Array.isArray(nested.data)) {
    return nested;
  }

  return {
    data: [],
    meta: { current_page: 1, last_page: 1, per_page: 20, total: 0, from: null, to: null },
  } as PaginatedResult<StudentProgressRow>;
}

export const learningAnalyticsService = {
  async instructorCourseAnalytics(courseId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: CourseAnalytics }>>(
      `/instructors/courses/${courseId}/analytics`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async instructorStudentsProgress(courseId: number | string, params: StudentsProgressQuery = {}) {
    const res = await api.get<ApiEnvelope<PaginatedResult<StudentProgressRow>>>(
      `/instructors/courses/${courseId}/students-progress`,
      { params, meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapPaginatedStudents(res.data);
  },

  async instructorStudentCourseProgress(courseId: number | string, studentId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: StudentCourseProgressDetail }>>(
      `/instructors/courses/${courseId}/students/${studentId}/progress`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async instructorStudentLearningSummary(studentId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: StudentLearningSummary }>>(
      `/instructors/students/${studentId}/learning-summary`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async adminOverview(params: Record<string, unknown> = {}) {
    const res = await api.get<ApiEnvelope<{ data: AdminLearningOverview }>>(
      "/mcp/learning-analytics/overview",
      { params, meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async adminCourseAnalytics(courseId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: CourseAnalytics }>>(
      `/mcp/courses/${courseId}/learning-analytics`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async adminStudentLearningSummary(studentId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: StudentLearningSummary }>>(
      `/mcp/students/${studentId}/learning-summary`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },

  async adminStudentLearningSummaryBySearch(search: string) {
    const res = await api.get<ApiEnvelope<{ data: StudentLearningSummary }>>(
      "/mcp/learning-analytics/student-summary",
      { params: { search }, meta: { toastSuccess: false, toastError: false } }
    );
    return unwrapData(res.data);
  },
};

export function exportStudentsProgressCsv(rows: StudentProgressRow[], isArabic: boolean) {
  const headers = isArabic
    ? [
        "الطالب",
        "تاريخ التسجيل",
        "حالة الوصول",
        "نسبة التقدم",
        "العناصر المكتملة",
        "إجمالي العناصر",
        "متوسط الاختبارات",
        "آخر نشاط",
        "الحالة",
      ]
    : [
        "Student",
        "Enrolled At",
        "Access Status",
        "Progress %",
        "Completed Items",
        "Total Items",
        "Quiz Average",
        "Last Activity",
        "Status",
      ];

  const statusLabels: Record<ProgressStatus, { ar: string; en: string }> = {
    not_started: { ar: "لم يبدأ", en: "Not started" },
    in_progress: { ar: "قيد التقدم", en: "In progress" },
    completed: { ar: "مكتمل", en: "Completed" },
    inactive: { ar: "غير نشط", en: "Inactive" },
  };

  const accessLabels: Record<AccessStatus, { ar: string; en: string }> = {
    active: { ar: "نشط", en: "Active" },
    not_started: { ar: "لم يبدأ", en: "Not started" },
    expired: { ar: "منتهي", en: "Expired" },
  };

  const escape = (value: string | number | null | undefined) => {
    const str = value == null ? "" : String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines = [
    headers.join(","),
    ...rows.map((row) =>
      [
        escape(row.student_name),
        escape(row.enrolled_at),
        escape(isArabic ? accessLabels[row.access_status].ar : accessLabels[row.access_status].en),
        escape(row.progress_percentage),
        escape(row.completed_items_count),
        escape(row.total_items_count),
        escape(row.average_quiz_score),
        escape(row.last_accessed_at),
        escape(isArabic ? statusLabels[row.status].ar : statusLabels[row.status].en),
      ].join(",")
    ),
  ];

  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = isArabic ? "تقدم-الطلاب.csv" : "students-progress.csv";
  link.click();
  URL.revokeObjectURL(url);
}
